// API helpers for /api/quotes/:id/submit + /api/quotes/:id/checkout.
//
// Submission shapes mirror the backend's SubmissionResultDto. We keep
// the type local to the portal package; promote to shared/ when a
// non-portal consumer needs it (sales tool, etc.).

import type { SalesRepDto } from 'shared';

import { api } from './client.js';

// Captured by the SPA's identity-prompt modal when a contact user
// (portal CustomerUser → Nexus Contact mapping) without a Nexus
// relationship submits a quote. Sent on the re-submit after the modal.
export type ContactUserIdentityInput = {
  name: string;
  company_name?: string;
};

export type SubmissionResultDto =
  | {
      kind: 'submitted';
      quote_id: string;
      order_id: string;
      total_sale_amount: number;
      requires_payment: boolean;
    }
  | {
      kind: 'repriced';
      quote_id: string;
      previous_total_sale_amount: number;
      new_total_sale_amount: number;
      delta_cents: number;
      message: string;
    }
  | {
      kind: 'pending_approval';
      quote_id: string;
      reason: string;
      message: string;
    }
  | {
      kind: 'identity_required';
      quote_id: string;
      message: string;
    };

export type SubmitResponse =
  | { ok: true; submission: SubmissionResultDto }
  | {
      ok: false;
      failure: {
        code: string;
        message: string;
        fallback_action: string;
        // The rep Nexus names on a refusal it sends to sales (FB-0026).
        sales_rep?: SalesRepDto;
        details?: Record<string, unknown>;
      };
    };

export async function submitQuote(
  id: string,
  contact_user_identity?: ContactUserIdentityInput,
): Promise<SubmitResponse> {
  // Always read the response body — the route returns 4xx/5xx with a
  // structured body, so axios's default-throw on non-2xx would lose it.
  // validateStatus opts in to body-reading on every response.
  const body = contact_user_identity ? { contact_user_identity } : undefined;
  const res = await api.post<unknown>(`/quotes/${id}/submit`, body, {
    validateStatus: () => true,
  });
  return normalizeSubmitResponse(res.data);
}

// The submit endpoint answers in TWO shapes:
//   1. The route's own outcome envelope — { ok: true, submission } on
//      success, or { ok: false, failure } for an ERP-code failure.
//   2. A thrown-error envelope — { error: { code, message, details } } —
//      emitted by the error middleware for any precondition the request
//      tripped BEFORE the ERP call: the artwork-required gate, an
//      unpriced/expired quote, missing identity, etc. (applies to every
//      product type).
// The caller only understands shape 1, so we fold shape 2 into a
// `failure` here. Without this, reading `result.failure.*` on an
// `{ error }` body throws and the customer sees a generic
// "Submission failed" with the real reason (e.g. "upload your artwork")
// swallowed.
function normalizeSubmitResponse(data: unknown): SubmitResponse {
  if (data && typeof data === 'object') {
    const d = data as Record<string, unknown>;
    if (d.ok === true || d.ok === false) return d as SubmitResponse;

    if ('error' in d && d.error && typeof d.error === 'object') {
      const e = d.error as {
        code?: string;
        message?: string;
        fallback_action?: string;
        details?: Record<string, unknown>;
      };
      // The gate's customer sentence (e.g. "Upload your artwork before
      // placing the order.") is the envelope's own message.
      const message = e.message ?? 'We could not place your order. Please try again.';
      return {
        ok: false,
        failure: {
          code: e.code ?? 'SUBMIT_FAILED',
          message,
          fallback_action: e.fallback_action ?? 'edit_config',
          ...(e.details ? { details: e.details } : {}),
        },
      };
    }
  }
  // Unrecognized shape — fail closed with a safe, generic message.
  return {
    ok: false,
    failure: {
      code: 'SUBMIT_FAILED',
      message: 'We could not place your order. Please try again.',
      fallback_action: 'retry',
    },
  };
}

export type CheckoutResponse = {
  payment_id: string;
  provider: string;
  amount_cents: number;
  currency: string;
  client_action_token: string;
};

export async function startCheckout(id: string): Promise<CheckoutResponse> {
  const res = await api.post<{ ok: true; checkout: CheckoutResponse }>(
    `/quotes/${id}/checkout`
  );
  return res.data.checkout;
}

